import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { uiConfig } from '../../config/appConfig';
import {
  Button,
  Input,
  Select,
  SelectItem,
  MultiSelect,
  DateSelect,
  Dropdown,
  DropdownItem,
  DropdownSeparator,
  FilterPresets
} from '../ui';
import { SearchIcon, FilterIcon, ChevronDownIcon, XIcon, CheckIcon, DocumentTextIcon } from '../ui/Icons';

/**
 * FilterSection - Handles all filtering UI and logic
 * Extracted from GenericFormsPage for better maintainability
 */
const FilterSection = ({
  // Props
  config,
  entityName,
  fields,
  data,
  labels,
  
  // Filter state
  filtersVisible,
  setFiltersVisible,
  unifiedFilters,
  setUnifiedFilters,
  activeFilterTab,
  setActiveFilterTab,
  
  // Filter presets
  savedFilterPresets,
  setSavedFilterPresets,
  showFilterPresets,
  setShowFilterPresets,
  
  // Callbacks
  onApplyFilterPreset,
  onRefreshFilterPresets,
  onClearAllFilters,
  
  // Search
  searchTerm,
  setSearchTerm
}) => {
  const { theme } = useTheme();

  // Get unique filter options for select fields
  const getFilterOptions = (fieldName) => {
    const field = fields.find(f => f.name === fieldName);
    
    if (field && field.options && field.options.length > 0) {
      return field.options.map(opt => opt.label || opt.value || opt);
    }
    
    // For lookup fields with displayField, use the display values instead of IDs
    if (field?.table?.displayField && field?.lookupEndpoint) {
      const targetField = field.table.displayField;
      const uniqueValues = [...new Set(data.map(item => item[targetField]).filter(Boolean))];
      return uniqueValues.sort();
    }
    
    // For lookup fields without displayField, extract name from objects
    if (field?.lookupEndpoint) {
      const sourceField = fieldName.replace('Id', ''); // Remove 'Id' suffix
      const uniqueValues = [...new Set(data.map(item => {
        const obj = item[sourceField];
        if (obj && typeof obj === 'object') {
          // Extract name property from object (same logic as GenericModulePage)
          return obj.name || obj.typeName || obj.statusName || obj.title || 'Unknown';
        }
        return obj;
      }).filter(Boolean))];
      return uniqueValues.sort();
    }
    
    // Default: use raw field values
    const uniqueValues = [...new Set(data.map(item => item[fieldName]).filter(Boolean))];
    return uniqueValues.sort();
  };

  // Get all filterable fields (combines all filter types)
  const getAllFilterableFields = () => {
    // If filterableFields is manually specified in config, use that
    if (config.filterableFields && Array.isArray(config.filterableFields)) {
      return fields.filter(field => 
        config.filterableFields.includes(field.name) && field.type !== 'hidden'
      );
    }
    
    // Auto-detect filterable fields (select, date, number, boolean, radio, textarea, or text with reasonable options)
    return fields.filter(field => {
      if (field.type === 'select' || field.type === 'date' || field.type === 'number') return true;
      if (field.type === 'switch' || field.type === 'toggle' || field.type === 'checkbox' || field.type === 'boolean') return true;
      if (field.type === 'radio' || field.type === 'textarea') return true;
      if (field.type === 'hidden') return false;
      
      // Include text fields that have reasonable number of unique values for filtering
      const uniqueValues = [...new Set(data.map(item => item[field.name]).filter(Boolean))];
      return uniqueValues.length > 1 && uniqueValues.length <= 20;
    });
  };

  // Get filterable fields for the current tab (when filter groups are configured)
  const getFilterableFieldsForTab = (tabName = activeFilterTab) => {
    const allFilterableFields = getAllFilterableFields();
    
    // If no filter groups are configured, return all filterable fields
    if (!config.filterGroups || config.filterGroups.length === 0) {
      return allFilterableFields;
    }
    
    // Find the specific tab configuration
    const tabConfig = config.filterGroups.find(group => group.name === tabName);
    if (!tabConfig) {
      return allFilterableFields;
    }
    
    // Filter fields based on the tab's field list
    return allFilterableFields.filter(field => 
      tabConfig.fields.includes(field.name)
    );
  };

  // Get active filter count for a specific tab
  const getActiveFilterCountForTab = (tabName) => {
    if (!config.filterGroups || config.filterGroups.length === 0) {
      return 0;
    }
    
    const tabConfig = config.filterGroups.find(group => group.name === tabName);
    if (!tabConfig) {
      return 0;
    }
    
    // Count active filters that belong to this tab
    let count = 0;
    tabConfig.fields.forEach(fieldName => {
      const filter = unifiedFilters[fieldName];
      if (filter) {
        const isActive = (
          (filter.type === 'select' && filter.value) ||
          (filter.type === 'multiSelect' && filter.values && filter.values.length > 0) ||
          (filter.type === 'dateRange' && (filter.from || filter.to || filter.preset)) ||
          (filter.type === 'numericRange' && (filter.min !== undefined || filter.max !== undefined)) ||
          (filter.type === 'boolean' && filter.value !== undefined) ||
          (filter.type === 'text' && filter.value)
        );
        if (isActive) count++;
      }
    });
    
    return count;
  };

  // Update unified filter
  const updateUnifiedFilter = (fieldName, filterConfig) => {
    setUnifiedFilters(prev => {
      const newFilters = { ...prev };
      
      // Remove filter if it's empty/undefined
      if (!filterConfig || 
          (filterConfig.type === 'select' && !filterConfig.value) ||
          (filterConfig.type === 'multiSelect' && (!filterConfig.values || filterConfig.values.length === 0)) ||
          (filterConfig.type === 'dateRange' && !filterConfig.from && !filterConfig.to && !filterConfig.preset) ||
          (filterConfig.type === 'numericRange' && filterConfig.min === undefined && filterConfig.max === undefined) ||
          (filterConfig.type === 'boolean' && filterConfig.value === undefined) ||
          (filterConfig.type === 'text' && !filterConfig.value)) {
        delete newFilters[fieldName];
      } else {
        newFilters[fieldName] = filterConfig;
      }
      
      return newFilters;
    });
  };

  // Remove unified filter
  const removeUnifiedFilter = (fieldName) => {
    setUnifiedFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[fieldName];
      return newFilters;
    });
  };

  // Top controls (search, filter button, all filters preset)
  const renderTopControls = () => (
    <div className="flex items-center gap-2 flex-1" style={{ maxWidth: uiConfig.searchInputMaxWidth }}>
      <div className="relative flex-1">
        <Input
          type="text"
          placeholder={labels.searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
        <SearchIcon className={`absolute left-3 top-3 w-4 h-4 ${theme.text.muted}`} />
      </div>

      {/* Enhanced Filter Toggle Button */}
      {getAllFilterableFields().length > 0 && (
        <button
          onClick={() => setFiltersVisible(!filtersVisible)}
          className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-all duration-300 group ${
            filtersVisible 
              ? `${theme.colors.primary} ${theme.border.primary} text-white shadow-lg hover:shadow-xl transform scale-105` 
              : `${theme.colors.background} ${theme.border.light} ${theme.text.primary} hover:${theme.colors.backgroundHover} shadow-sm hover:shadow-md`
          }`}
          title={filtersVisible ? labels.hideFiltersTooltip : labels.showFiltersTooltip}
        >
          <FilterIcon 
            className={`w-4 h-4 transition-transform duration-300 ${filtersVisible ? 'rotate-180' : ''}`}
          />
          <span className="font-medium">
            {filtersVisible ? 'Hide Filters' : 'Show Filters'}
          </span>
          
          {/* Enhanced Active filters indicator */}
          {Object.keys(unifiedFilters).length > 0 && (
            <div className={`flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold transition-all duration-200 ${
              filtersVisible 
                ? 'bg-white/20 text-white' 
                : `${theme.colors.accent} text-white shadow-sm`
            }`}>
              {Object.keys(unifiedFilters).length}
            </div>
          )}
          
          {/* Animated chevron indicator */}
          <ChevronDownIcon 
            className={`w-3 h-3 transition-transform duration-300 ${filtersVisible ? 'rotate-180' : ''}`}
            strokeWidth={2.5}
          />
        </button>
      )}

      {/* Modern Filter Preset Selector */}
      {(() => {
        const hasFilterableFields = getAllFilterableFields().length > 0;
        const hasPresets = savedFilterPresets.length > 0;

        return hasFilterableFields && hasPresets;
      })() && (
        <div className="relative">
          <Dropdown
            className="w-64"
            trigger={
              <Button
                variant="outline"
                className="h-10 w-auto px-3 text-sm min-w-[180px] justify-between"
                title={labels.quickFiltersTooltip}
              >
                <div className="flex items-center gap-2">
                  <FilterIcon className="w-4 h-4 text-blue-600" />
                  <span className="truncate">
                    {(() => {
                      const activePreset = savedFilterPresets.find(preset => 
                        JSON.stringify(preset.filters) === JSON.stringify(unifiedFilters)
                      );
                      return activePreset ? activePreset.name : labels.allFiltersText;
                    })()}
                  </span>
                  {Object.keys(unifiedFilters).length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 rounded">
                      {Object.keys(unifiedFilters).length}
                    </span>
                  )}
                </div>
                <ChevronDownIcon className="w-4 h-4 text-gray-400" />
              </Button>
            }
          >
            {/* Clear All Option */}
            <DropdownItem
              onClick={() => onApplyFilterPreset('')}
              className={Object.keys(unifiedFilters).length === 0 ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <XIcon className="w-4 h-4 text-gray-400" />
                  <span>{labels.allFiltersText}</span>
                </div>
                {Object.keys(unifiedFilters).length === 0 && (
                  <CheckIcon className="w-4 h-4 text-blue-600" />
                )}
              </div>
            </DropdownItem>

            {savedFilterPresets.length > 0 && <DropdownSeparator />}

            {/* Saved Presets */}
            {savedFilterPresets.map(preset => {
              const isActive = JSON.stringify(preset.filters) === JSON.stringify(unifiedFilters);
              const isDefault = localStorage.getItem(`default-filter-preset-${entityName.toLowerCase()}`) === preset.id;
              
              return (
                <DropdownItem
                  key={preset.id}
                  onClick={() => onApplyFilterPreset(preset.id)}
                  className={isActive ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      <FilterIcon className="w-4 h-4 text-blue-600" />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{preset.name}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {(() => {
                            const activeFilters = Object.entries(preset.filters || {}).filter(([key, filterConfig]) => {
                              if (filterConfig.type === 'multiSelect') return filterConfig.values && filterConfig.values.length > 0;
                              if (filterConfig.type === 'dateRange') return filterConfig.from || filterConfig.to || filterConfig.preset;
                              if (filterConfig.type === 'numericRange') return filterConfig.min !== undefined || filterConfig.max !== undefined;
                              if (filterConfig.type === 'boolean') return filterConfig.value !== undefined;
                              if (filterConfig.type === 'text') return filterConfig.value;
                              return filterConfig.value;
                            });
                            return activeFilters.length === 1 ? '1 filter' : `${activeFilters.length} filters`;
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {isDefault && (
                        <span className="px-1.5 py-0.5 text-xs bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200 rounded">
                          Default
                        </span>
                      )}
                      {isActive && (
                        <CheckIcon className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                  </div>
                </DropdownItem>
              );
            })}
          </Dropdown>
        </div>
      )}
    </div>
  );

  // Filter panels (to be rendered at the bottom)
  const renderFilterPanels = () => (
    getAllFilterableFields().length > 0 && filtersVisible && (
      <div className={`px-2 mb-2 rounded-lg border transition-all duration-300 animate-in slide-in-from-top-2 fade-in ${
        Object.keys(unifiedFilters).length > 0
          ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800'
          : `${theme.colors.background} ${theme.border.light}`
      }`}>
        {/* Fixed Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          {/* Filter Header */}
          <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
              <FilterIcon className={`w-4 h-4 ${theme.text.primary}`} />
              <h3 className={`text-sm font-semibold ${theme.text.primary}`}>Filters</h3>
              
              {/* Active filters indicator - only show when filters are open */}
              {filtersVisible && Object.keys(unifiedFilters).length > 0 && (
                <span className={`text-xs text-white px-2 py-1 rounded-full font-medium ${theme.colors.primary}`}>
                  {Object.keys(unifiedFilters).length}
                </span>
              )}
              
              {/* Enhanced Filter Presets Button */}
              <div className="relative">
                <button
                  onClick={() => setShowFilterPresets(!showFilterPresets)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all duration-200 shadow-sm hover:shadow-md group ${theme.text.primary} ${theme.colors.backgroundSecondary} ${theme.border.light} hover:${theme.colors.backgroundHover} hover:${theme.border.primary}`}
                  title={labels.filterPresetsText}
                >
                  <DocumentTextIcon className={`w-3.5 h-3.5 transition-colors ${theme.text.secondary} group-hover:${theme.text.primary}`} />
                  <span className="font-medium">{labels.filterPresetsText}</span>
                  <ChevronDownIcon className={`w-3 h-3 transition-transform duration-200 ${showFilterPresets ? 'rotate-180' : ''} ${theme.text.secondary}`} />
                </button>
                
                <FilterPresets
                  currentFilters={unifiedFilters}
                  onApplyFilters={(filters) => setUnifiedFilters(filters)}
                  onPresetsChange={onRefreshFilterPresets}
                  tableId={entityName.toLowerCase()}
                  entityName={entityName}
                  isOpen={showFilterPresets}
                  onClose={() => setShowFilterPresets(false)}
                />
              </div>

              {/* Clear All Button - After Filter Presets */}
              {Object.keys(unifiedFilters).length > 0 && (
                <button
                  onClick={onClearAllFilters}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all duration-200 shadow-sm hover:shadow-md group ${theme.text.accent} ${theme.colors.backgroundSecondary} ${theme.border.light} hover:${theme.colors.backgroundHover} hover:${theme.border.accent}`}
                  title="Clear all active filters"
                >
                  <XIcon className={`w-3 h-3 transition-colors ${theme.text.accent} group-hover:${theme.text.primary}`} />
                  <span className="font-medium">{labels.clearAllText}</span>
                </button>
              )}
            </div>

            {/* Right side - Filter Group Tabs */}
            {config.filterGroups && config.filterGroups.length > 0 && (
              <>
                {/* Desktop Tabs */}
                <div className="hidden md:flex items-center gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
                  {config.filterGroups.map((group) => {
                    const filterCount = getActiveFilterCountForTab(group.name);
                    return (
                      <button
                        key={group.name}
                        onClick={() => setActiveFilterTab(group.name)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                          activeFilterTab === group.name
                            ? `${theme.colors.primary} text-white shadow-sm`
                            : `${theme.text.secondary} hover:${theme.colors.backgroundHover} hover:text-gray-900 dark:hover:text-gray-100`
                        }`}
                      >
                        <span className="text-sm">{group.icon}</span>
                        <span>{group.label}</span>
                        {filterCount > 0 && (
                          <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${
                            activeFilterTab === group.name
                              ? 'bg-white bg-opacity-20 text-white'
                              : `${theme.colors.primary} text-white`
                          }`}>
                            {filterCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Mobile Dropdown */}
                <div className="md:hidden relative">
                  <select
                    value={activeFilterTab}
                    onChange={(e) => setActiveFilterTab(e.target.value)}
                    className={`appearance-none pl-3 pr-8 py-2 text-xs font-medium rounded-lg border focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all duration-200 ${theme.colors.background} ${theme.border.light} ${theme.text.primary} focus:${theme.border.primary}`}
                  >
                    {config.filterGroups.map((group) => {
                      const filterCount = getActiveFilterCountForTab(group.name);
                      return (
                        <option key={group.name} value={group.name}>
                          {group.icon} {group.label}{filterCount > 0 ? ` (${filterCount})` : ''}
                        </option>
                      );
                    })}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronDownIcon className={`w-4 h-4 ${theme.text.secondary}`} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Filter Content */}
        <div className="p-4">
          {/* Filter Controls Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {getFilterableFieldsForTab().map((field) => {
              const hasFilter = unifiedFilters[field.name];
              const isActive = hasFilter && (
                (hasFilter.type === 'select' && hasFilter.value) ||
                (hasFilter.type === 'multiSelect' && hasFilter.values && hasFilter.values.length > 0) ||
                (hasFilter.type === 'dateRange' && (hasFilter.from || hasFilter.to || hasFilter.preset)) ||
                (hasFilter.type === 'numericRange' && (hasFilter.min !== undefined || hasFilter.max !== undefined)) ||
                (hasFilter.type === 'boolean' && hasFilter.value !== undefined) ||
                (hasFilter.type === 'text' && hasFilter.value)
              );

              return (
                <div key={`${field.name}-${JSON.stringify(unifiedFilters[field.name] || {})}`} className={`p-3 rounded-lg border transition-all duration-200 ${
                  isActive 
                    ? 'bg-white dark:bg-gray-900 border-blue-300 dark:border-blue-700 shadow-sm ring-1 ring-blue-200 dark:ring-blue-800' 
                    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-800'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {field.label}
                    </label>
                    {isActive && (
                      <button
                        onClick={() => removeUnifiedFilter(field.name)}
                        className="h-5 w-5 p-0 text-gray-400 hover:text-red-500"
                      >
                        <XIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  {/* Multi-Select Filter */}
                  {(field.type === 'select' || (field.type === 'text' && getFilterOptions(field.name).length <= 20)) && (
                    <MultiSelect
                      key={`${field.name}-multiselect-${JSON.stringify(unifiedFilters[field.name]?.values || [])}`}
                      options={getFilterOptions(field.name)}
                      value={unifiedFilters[field.name]?.values || []}
                      onChange={(values) => updateUnifiedFilter(field.name, {
                        type: 'multiSelect',
                        values: values
                      })}
                      placeholder={`${labels.allPrefix} ${field.label}`}
                      className="h-9 text-sm"
                      maxDisplay={1}
                    />
                  )}

                  {/* Enhanced Date Filter with Presets */}
                  {field.type === 'date' && (
                    <div key={`${field.name}-datefilter`} className="space-y-2">
                      {/* Modern Date Preset Selector */}
                      <DateSelect
                        value={unifiedFilters[field.name]?.preset || 'all'}
                        onChange={(preset) => {
                          if (preset === 'all') {
                            removeUnifiedFilter(field.name);
                            return;
                          }
                          
                          const now = new Date();
                          let from, to;
                          
                          switch (preset) {
                            case 'today':
                              from = to = now.toISOString().split('T')[0];
                              break;
                            case 'last-week':
                              from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                              to = now.toISOString().split('T')[0];
                              break;
                            case 'last-month':
                              // Last 30 days from today
                              const lastMonthDate = new Date(now);
                              lastMonthDate.setDate(now.getDate() - 30);
                              from = lastMonthDate.toISOString().split('T')[0];
                              to = now.toISOString().split('T')[0];
                              break;
                            case 'last-3-months':
                              // Last 90 days from today
                              const last3MonthsDate = new Date(now);
                              last3MonthsDate.setDate(now.getDate() - 90);
                              from = last3MonthsDate.toISOString().split('T')[0];
                              to = now.toISOString().split('T')[0];
                              break;
                            case 'last-6-months':
                              // Last 180 days from today
                              const last6MonthsDate = new Date(now);
                              last6MonthsDate.setDate(now.getDate() - 180);
                              from = last6MonthsDate.toISOString().split('T')[0];
                              to = now.toISOString().split('T')[0];
                              break;
                            case 'last-year':
                              // Last 365 days from today
                              const lastYearDate = new Date(now);
                              lastYearDate.setDate(now.getDate() - 365);
                              from = lastYearDate.toISOString().split('T')[0];
                              to = now.toISOString().split('T')[0];
                              break;
                            case 'current-week':
                              // From Monday of current week to Sunday
                              const startOfWeek = new Date(now);
                              const dayOfWeek = now.getDay();
                              const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, Monday = 1
                              startOfWeek.setDate(now.getDate() - daysToMonday);
                              const endOfWeek = new Date(startOfWeek);
                              endOfWeek.setDate(startOfWeek.getDate() + 6);
                              from = startOfWeek.toISOString().split('T')[0];
                              to = endOfWeek.toISOString().split('T')[0];
                              break;
                            case 'current-month':
                              from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
                              to = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
                              break;
                            case 'current-year':
                              from = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
                              to = new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0];
                              break;
                            case 'custom':
                              // Keep existing dates or clear them
                              from = unifiedFilters[field.name]?.from;
                              to = unifiedFilters[field.name]?.to;
                              break;
                            default:
                              return;
                          }
                          
                          const filterConfig = {
                            type: 'dateRange',
                            preset: preset,
                            from: from,
                            to: to
                          };
                          updateUnifiedFilter(field.name, filterConfig);
                        }}
                        className="h-9"
                      />
                      
                      {/* Custom Date Range Inputs - Only show when "Custom" is selected */}
                      {unifiedFilters[field.name]?.preset === 'custom' && (
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            key={`${field.name}-from-${unifiedFilters[field.name]?.from || 'empty'}`}
                            type="date"
                            value={unifiedFilters[field.name]?.from || ''}
                            onChange={(e) => updateUnifiedFilter(field.name, {
                              ...unifiedFilters[field.name],
                              type: 'dateRange',
                              preset: 'custom',
                              from: e.target.value || undefined
                            })}
                            className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded h-7 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 flex-shrink-0">to</span>
                          <input
                            key={`${field.name}-to-${unifiedFilters[field.name]?.to || 'empty'}`}
                            type="date"
                            value={unifiedFilters[field.name]?.to || ''}
                            onChange={(e) => updateUnifiedFilter(field.name, {
                              ...unifiedFilters[field.name],
                              type: 'dateRange',
                              preset: 'custom',
                              to: e.target.value || undefined
                            })}
                            className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded h-7 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      )}
                      
                      {/* Date Range Display - Show selected range for non-custom presets */}
                      {unifiedFilters[field.name]?.preset && unifiedFilters[field.name]?.preset !== 'custom' && unifiedFilters[field.name]?.preset !== 'all' && (
                        <div className={`text-xs px-2 py-1 rounded ${theme.colors.backgroundSecondary} ${theme.text.secondary}`}>
                          {unifiedFilters[field.name]?.from === unifiedFilters[field.name]?.to 
                            ? unifiedFilters[field.name]?.from
                            : `${unifiedFilters[field.name]?.from || '...'} to ${unifiedFilters[field.name]?.to || '...'}`
                          }
                        </div>
                      )}
                    </div>
                  )}

                  {/* Numeric Range Filter */}
                  {field.type === 'number' && (
                    <div className="flex items-center gap-2 min-w-0 h-9">
                      <input
                        type="number"
                        value={unifiedFilters[field.name]?.min || ''}
                        onChange={(e) => updateUnifiedFilter(field.name, {
                          ...unifiedFilters[field.name],
                          type: 'numericRange',
                          min: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        className={`flex-1 min-w-0 px-2 py-1 text-sm border rounded h-full ${theme.colors.surface} ${theme.text.primary} ${theme.border.light} focus:${theme.border.light}`}
                        placeholder={labels.minPlaceholder}
                      />
                      <span className="text-gray-400 text-sm font-medium flex-shrink-0">-</span>
                      <input
                        type="number"
                        value={unifiedFilters[field.name]?.max || ''}
                        onChange={(e) => updateUnifiedFilter(field.name, {
                          ...unifiedFilters[field.name],
                          type: 'numericRange',
                          max: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        className={`flex-1 min-w-0 px-2 py-1 text-sm border rounded h-full ${theme.colors.surface} ${theme.text.primary} ${theme.border.light} focus:${theme.border.light}`}
                        placeholder={labels.maxPlaceholder}
                      />
                    </div>
                  )}

                  {/* Boolean Filter (Switch/Toggle/Checkbox) */}
                  {(field.type === 'switch' || field.type === 'toggle' || field.type === 'checkbox' || field.type === 'boolean') && (
                    <div className="flex items-center gap-2 h-9">
                      <button
                        onClick={() => updateUnifiedFilter(field.name, {
                          type: 'boolean',
                          value: true
                        })}
                        className={`flex-1 px-3 py-1.5 text-xs font-medium rounded border transition-all ${
                          unifiedFilters[field.name]?.value === true
                            ? `${theme.colors.primary} ${theme.border.primary} text-white`
                            : `${theme.colors.background} ${theme.border.light} ${theme.text.secondary} hover:${theme.colors.backgroundHover}`
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => updateUnifiedFilter(field.name, {
                          type: 'boolean',
                          value: false
                        })}
                        className={`flex-1 px-3 py-1.5 text-xs font-medium rounded border transition-all ${
                          unifiedFilters[field.name]?.value === false
                            ? `${theme.colors.primary} ${theme.border.primary} text-white`
                            : `${theme.colors.background} ${theme.border.light} ${theme.text.secondary} hover:${theme.colors.backgroundHover}`
                        }`}
                      >
                        No
                      </button>
                      <button
                        onClick={() => removeUnifiedFilter(field.name)}
                        className={`flex-1 px-3 py-1.5 text-xs font-medium rounded border transition-all ${theme.colors.backgroundSecondary} ${theme.border.light} ${theme.text.muted} hover:${theme.colors.backgroundHover}`}
                        title="Show All"
                      >
                        All
                      </button>
                    </div>
                  )}

                  {/* Radio Group Filter */}
                  {field.type === 'radio' && (
                    <Select
                      key={`${field.name}-radio-${unifiedFilters[field.name]?.value || 'all'}`}
                      value={unifiedFilters[field.name]?.value || ''}
                      onChange={(value) => {
                        if (value) {
                          updateUnifiedFilter(field.name, {
                            type: 'select',
                            value: value
                          });
                        } else {
                          removeUnifiedFilter(field.name);
                        }
                      }}
                      className="h-9 text-sm"
                    >
                      <SelectItem value="">{labels.allPrefix} {field.label}</SelectItem>
                      {(field.options || []).map((option) => (
                        <SelectItem 
                          key={typeof option === 'object' ? option.value : option} 
                          value={typeof option === 'object' ? option.value : option}
                        >
                          {typeof option === 'object' ? option.label : option}
                        </SelectItem>
                      ))}
                    </Select>
                  )}

                  {/* Textarea Filter (Text search) */}
                  {field.type === 'textarea' && (
                    <Input
                      key={`${field.name}-textarea-${unifiedFilters[field.name]?.value || 'empty'}`}
                      type="text"
                      value={unifiedFilters[field.name]?.value || ''}
                      onChange={(e) => {
                        if (e.target.value.trim()) {
                          updateUnifiedFilter(field.name, {
                            type: 'text',
                            value: e.target.value.trim()
                          });
                        } else {
                          removeUnifiedFilter(field.name);
                        }
                      }}
                      placeholder={`Search ${field.label.toLowerCase()}...`}
                      className="h-9 text-sm"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    )
  );

  return {
    topControls: renderTopControls(),
    filterPanels: renderFilterPanels()
  };
};

export default FilterSection;
